import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCategoriesStore } from "../store/categoriesStore";
import { useProductStore } from "../store/productStore";
import CatalogContainer from "../components/catalog/CatalogContainer";
import { ProductFilters } from "../types";

function CatalogPage() {
  const {
    products,
    total,
    fetchAllProducts,
    filters,
    setFilter,
    loading: productsLoading,
    error: productsError,
  } = useProductStore();
  const {
    categories,
    fetchCategories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategoriesStore();

  const navigate = useNavigate();
  const location = useLocation();

  const [localFilters, setLocalFilters] = useState<ProductFilters>(filters);
  const [activeSort, setActiveSort] = useState(filters.sort_by || "created_at");

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters: Record<string, any> = {};
    params.forEach((value, key) => {
      if (key === "tags") newFilters[key] = value.split(",");
      else if (["min_price", "max_price", "offset", "limit", "category_id"].includes(key))
        newFilters[key] = Number(value);
      else if (["inStock", "onSale"].includes(key)) newFilters[key] = value === "true";
      else newFilters[key] = value;
    });
    (Object.keys(newFilters) as (keyof ProductFilters)[]).forEach((key) => {
      setFilter(key, newFilters[key]);
    });
  }, [location.search, setFilter]);

  useEffect(() => {
    fetchAllProducts();
  }, [filters, fetchAllProducts]);

  // После редактирования товара в админке каталог может показывать старый список.
  // При возврате на вкладку с каталогом обновляем список, чтобы подтянуть новые картинки.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchAllProducts();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchAllProducts]);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) params.set(key, value.join(","));
        else params.set(key, String(value));
      }
    });
    navigate(`/catalog?${params.toString()}`, { replace: true });
  }, [filters, navigate]);

  const updatePage = (page: number) => {
    const updatedFilters = { ...localFilters, offset: (page - 1) * filters.limit };
    setLocalFilters(updatedFilters);
    applyFilters(updatedFilters);
  };

  const handleSortChange = (
    sortBy: "created_at" | "price" | "name" | "rating",
    order: "asc" | "desc"
  ) => {
    setActiveSort(sortBy);
    setLocalFilters({ ...localFilters, sort_by: sortBy, order });
    applyFilters({ ...localFilters, sort_by: sortBy, order, offset: 0 });
  };

  const applyFilters = (filters: ProductFilters) => {
    Object.entries(filters).forEach(([key, value]) => {
      setFilter(key as keyof ProductFilters, value);
    });
  };

  const resetFilters = () => {
    setLocalFilters({
      limit: 10,
      offset: 0
    });
    setFilter("min_price", undefined);
    setFilter("max_price", undefined);
    setFilter("category_id", undefined);
    setFilter("tags", undefined);
    setFilter("inStock", undefined);
    setFilter("onSale", undefined);
  };

  return (
    <CatalogContainer
      products={products}
      total={total}
      filters={localFilters}
      updatePage={updatePage}
      activeSort={activeSort}
      handleSortChange={handleSortChange}
      categories={categories}
      categoriesLoading={categoriesLoading}
      categoriesError={categoriesError}
      productsLoading={productsLoading}
      productsError={productsError}
      applyFilters={applyFilters}
      resetFilters={resetFilters}
    />
  );
}

export default CatalogPage;
